import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, MapPin, Phone, Mail } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/content";
import Logo from "@/components/Logo";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Logo variant="dark" to="/" />
            <p className="mt-6 text-sm leading-relaxed text-slate-400">
              India's trusted partner for recruitment, contract staffing, payroll management and HR consultancy services.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Linkedin, href: COMPANY.social.linkedin, label: "LinkedIn" },
                { Icon: Facebook, href: COMPANY.social.facebook, label: "Facebook" },
                { Icon: Instagram, href: COMPANY.social.instagram, label: "Instagram" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-primary flex items-center justify-center transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-4 h-4 text-slate-300" strokeWidth={1.75} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-heading font-bold text-sm uppercase tracking-[0.18em] mb-5">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              {[
                ["Home", "/"], ["About Us", "/about"], ["Services", "/services"],
                ["Careers", "/careers"], ["Join Talent Pool", "/join"],
                ["Recruitment Partner Login", "/login"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-slate-400 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-heading font-bold text-sm uppercase tracking-[0.18em] mb-5">Services</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-slate-400 hover:text-white transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-heading font-bold text-sm uppercase tracking-[0.18em] mb-5">Get in Touch</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" strokeWidth={1.75} />
                <span className="text-slate-400 leading-relaxed">
                  {COMPANY.address.line1},<br />
                  {COMPANY.address.line2},<br />
                  {COMPANY.address.city}, {COMPANY.address.state}
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-primary shrink-0" strokeWidth={1.75} />
                <div className="space-y-0.5">
                  <a href={`tel:${COMPANY.phoneRaw}`} data-testid="footer-phone" className="block text-slate-400 hover:text-white">
                    {COMPANY.phone} <span className="text-xs text-slate-500">— Clients</span>
                  </a>
                  <a href={`tel:${COMPANY.candidatePhoneRaw}`} data-testid="footer-candidate-phone" className="block text-slate-400 hover:text-white text-xs">
                    {COMPANY.candidatePhone} <span className="text-slate-500">— Candidates / WhatsApp</span>
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-primary shrink-0" strokeWidth={1.75} />
                <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="text-slate-400 hover:text-white">{COMPANY.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
          <p>Recruitment • Staffing • Payroll • HR Consulting — Pan India</p>
        </div>
      </div>
    </footer>
  );
}
